import { RoutalPalette } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';

import { Grid } from '@mui/material';
import More3Points from '../../../../assets/svg/More3Points';
import { getPlanStatusColor } from '../../../../constants/Colors';
import { PlanFlat } from '../../../../domain/plan/PlanFlat';
import { PlanActionsMenu } from '../PlanActionsMenu';
import { PlanPill } from '../PlanPill';
import { useManagePlanQuery } from '../hooks/useManagePlanQuery';

interface PlannerPlansDayListProps {
  plan: PlanFlat;
}

export const PlannerPlansDayPlanPill = ({ plan }: PlannerPlansDayListProps) => {
  const { navigateToPlan, clearPlanQueries } = useManagePlanQuery();
  const [isHovered, setIsHovered] = useState(false);
  const statusColorMedium = getPlanStatusColor(plan.status, isHovered ? `dark` : undefined);

  const [eventContextAnchor, setEventContextAnchor] = useState<
    | {
        top: number;
        left: number;
      }
    | undefined
  >(undefined);

  const handleCloseContextMenu = () => {
    setEventContextAnchor(undefined);
  };

  useEffect(() => {
    if (eventContextAnchor) {
      const handleClickOutside = (event: MouseEvent) => {
        handleCloseContextMenu();
      };

      document.addEventListener(`click`, handleClickOutside);
      return () => {
        document.removeEventListener(`click`, handleClickOutside);
      };
    }
    return () => {};
  }, [eventContextAnchor]);

  return (
    <>
      <div
        style={{ position: `relative` }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setEventContextAnchor({
            top: e.clientY,
            left: e.clientX,
          });
        }}
      >
        <PlanPill
          plan={plan}
          withTooltip
          onClick={() => navigateToPlan({ planId: plan.id })}
          style={{
            border: `1px solid ${statusColorMedium}`,
            borderRadius: `10px`,
            padding: `4px`,
            paddingRight: `10px`,
          }}
        />

        {isHovered && (
          <Grid
            container
            style={{
              position: `absolute`,
              top: `1px`,
              right: `1px`,
              width: `50px`,
              height: `calc(100% - 2px)`,
              background: `linear-gradient(90deg, ${RoutalPalette.neutral02n + `00`} 0%, ${
                RoutalPalette.neutral02n
              } 30%, ${RoutalPalette.neutral02n} 100%)`,
              borderRadius: `0 10px 10px 0`,
            }}
            justifyContent="center"
            alignContent="center"
          >
            <PlanActionsMenu
              type="popover"
              plans={[plan]}
              popoverProps={{
                hideArrow: true,
                content: <More3Points />,
                style: {
                  color: RoutalPalette.secondary60,
                  padding: 0,
                  margin: 0,
                  border: `unset`,
                },
              }}
              onCloseAnyDialog={() => {
                setIsHovered(false);
              }}
              onDeletePlan={(deletedPlans: Array<PlanFlat>) => {
                clearPlanQueries();
              }}
              onUpdatePlanStatus={() => {
                clearPlanQueries();
              }}
              onDuplicatePlan={() => {
                clearPlanQueries();
              }}
            />
          </Grid>
        )}
      </div>
      {eventContextAnchor && (
        <PlanActionsMenu
          type="contextual"
          plans={[plan]}
          contextAnchor={{ screenPosition: eventContextAnchor }}
          onCloseAnyDialog={() => {
            setEventContextAnchor(undefined);
          }}
          onDeletePlan={(deletedPlans: Array<PlanFlat>) => {
            clearPlanQueries();
          }}
          onUpdatePlanStatus={() => {
            clearPlanQueries();
          }}
          onDuplicatePlan={() => {
            clearPlanQueries();
          }}
        />
      )}
    </>
  );
};
